import { motion } from "framer-motion";
import { MessageSquareText, ChefHat, Sparkles, Heart, Users, Cake, PartyPopper, Star, Sun } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import InquiryForm from "@/components/InquiryForm";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const STEPS = [
  { icon: MessageSquareText, title: "Ci scrivi", text: "Ci racconti data, luogo e quante bocche ci sono da sfamare. Anche solo un'idea va bene." },
  { icon: ChefHat, title: "Costruiamo il menu", text: "Lo decidiamo insieme: dolce, salato, vegetariano, vegano. Seguiamo stagione e intolleranze." },
  { icon: Sparkles, title: "Ci pensiamo noi", text: "Arriviamo, cuciniamo, apparecchiamo. Tu fai gli onori di casa, noi sparecchiamo." },
];

const OCCASIONS = [
  { icon: Heart, label: "Brunch di coppia" },
  { icon: Users, label: "Brunch tra amici" },
  { icon: Cake, label: "Compleanni" },
  { icon: PartyPopper, label: "Piccole celebrazioni" },
  { icon: Star, label: "Occasioni speciali" },
  { icon: Sun, label: "Brunch in vacanza, per ospiti di B&B e case vacanza" },
];

const EVENT_OPTIONS = [
  "Brunch privato",
  "Brunch di coppia",
  "Brunch tra amici",
  "Compleanno",
  "Piccola celebrazione",
  "Ospiti B&B / casa vacanze",
  "Altro",
];

export default function Brunch() {
  useSeo({
    title: "Brunch a domicilio a Rimini | Brunch & Bottega",
    description: "Brunch preparato e servito a casa tua a Rimini e dintorni. Per coppie, amici, compleanni e piccole occasioni. Richiedi disponibilità.",
    path: "/brunch",
  });

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="brunch-hero">
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-5">Brunch a domicilio · Rimini e dintorni</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink">
            Il brunch viene da te. <em className="text-forest">Tu resta in pigiama.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-stone-600 max-w-xl">
            Prepariamo e serviamo il brunch a casa tua, o dove preferisci, a Rimini e dintorni.
            Tavola apparecchiata, cibo fatto a mano, cucina lasciata pulita. Come al bar, ma meglio: non devi nemmeno uscire.
          </p>
        </motion.div>
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-forest/10 border border-stone-200/60">
            <img
              src="/images/brunch-flatlay.jpg"
              alt="Tavolata di brunch vista dall'alto con piatti dolci e salati fatti in casa"
              className="w-full h-72 sm:h-96 object-cover"
              fetchPriority="high"
              data-testid="brunch-hero-image"
            />
          </div>
        </motion.div>
      </section>

      <section className="bg-sand/70 border-y border-stone-200/60" data-testid="brunch-how">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink">
            Come funziona
          </motion.h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.12 }}
                className="bg-white/80 border border-stone-200/80 rounded-2xl p-7 shadow-sm" data-testid={`brunch-step-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="font-display text-3xl text-terracotta">{i + 1}</span>
                  <s.icon size={24} className="text-forest" aria-hidden="true" />
                </div>
                <h3 className="font-display text-xl mt-4 text-ink">{s.title}</h3>
                <p className="mt-2 text-sm sm:text-base leading-relaxed text-stone-600">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="brunch-occasions">
        <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink max-w-2xl">
          Per quali occasioni? Quasi tutte quelle in cui si ha fame.
        </motion.h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {OCCASIONS.map((o, i) => (
            <motion.div key={o.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="flex items-center gap-3 bg-white/70 border border-stone-200/80 rounded-2xl px-5 py-4"
              data-testid={`occasion-${i}`}>
              <o.icon size={20} className="text-terracotta shrink-0" aria-hidden="true" />
              <p className="text-sm sm:text-base font-medium text-stone-800">{o.label}</p>
            </motion.div>
          ))}
        </div>
        <motion.p {...fadeUp} className="mt-8 text-sm text-stone-500 max-w-2xl">
          Ogni brunch è diverso: menu, quantità e orari si decidono insieme. Per questo non trovi prezzi fissi qui:
          preferiamo fare due chiacchiere e mandarti una proposta sensata.
        </motion.p>
      </section>

      <section className="bg-forest" data-testid="brunch-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-5 text-cream">
            <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-4">Richiedi disponibilità</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.2]">
              Raccontaci che brunch hai in testa.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-cream/75">
              Compila il modulo: ti rispondiamo noi, di persona, senza bot e senza newsletter nascoste.
              I tuoi dati servono solo a ricontattarti.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-7 bg-cream rounded-3xl p-6 sm:p-10 shadow-xl">
            <InquiryForm
              kind="brunch"
              variant="event"
              eventOptions={EVENT_OPTIONS}
              submitLabel="Richiedi disponibilità"
              testIdPrefix="brunch"
            />
          </motion.div>
        </div>
      </section>
    </>
  );
}
